/**
 * Invite a person, or edit one — the form behind both gestures on the Users
 * screen (routes P20 create, P22 update).
 *
 * Server validation binds to fields through `ApiClientError.fieldErrors()`
 * (§05.5): a 422 detail for `email` renders under the email input, not as a
 * toast the user has to map back to a control themselves. Details without a
 * field surface above the form.
 *
 * **Edit sends only what changed.** `PATCH /users/:ref` is strict and requires
 * at least one field, so the diff is computed against the row being edited —
 * an untouched form closes without a request, and a name-only change does not
 * re-assert the role set (which would turn a concurrent role edit into a lost
 * update).
 *
 * Outside production the API echoes `developmentInviteToken` so the invite
 * flow works before an email transport exists; when present, the modal turns
 * it into the copyable accept link the operator actually needs to hand over.
 */

import type { UpdateUserRequest, UserDto } from '@rd/shared';
import { useEffect, useState, type ReactElement } from 'react';

import { Button } from '../../components/Button.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Field } from '../../components/Field.js';
import { Input, Checkbox } from '../../components/Input.js';
import { Modal } from '../../components/Modal.js';
import { ApiClientError } from '../../lib/apiClient.js';
import { useRoles } from '../roles/useRoles.js';

import { useCreateUser, useUpdateUser } from './useUsers.js';

/**
 * The copyable accept link. Exported because the list page needs the identical
 * rendering for a *resent* invite's token — one shape for "here is the link",
 * wherever the token came from.
 *
 * The link is built on `window.location.origin` because the accept page lives
 * in this same SPA (`/invite/accept`, `router.tsx`) — the origin the operator
 * is on is by definition the origin the invitee must open.
 */
export function InviteAcceptLink({ token }: { token: string }): ReactElement {
  const url = `${window.location.origin}/invite/accept?token=${token}`;
  return (
    <div className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-line bg-surface-alt p-3">
      <p className="text-[length:var(--text-xs)] text-muted">
        Email delivery is not configured, so the invite was not sent automatically. Share this
        sign-up link with them instead:
      </p>
      <div className="flex items-center gap-[var(--space-2)]">
        <Input
          readOnly
          value={url}
          aria-label="Invite link"
          className="font-[family-name:var(--font-mono)] text-[length:var(--text-xs)]"
          onFocus={(event) => event.currentTarget.select()}
        />
        <Button
          variant="secondary"
          onClick={() => {
            // Clipboard access can be denied (permissions policy, http). The
            // read-only input above selects itself on focus, so manual copy
            // always remains possible — this button is a convenience, not the
            // only path.
            void navigator.clipboard?.writeText(url).catch(() => undefined);
          }}
        >
          Copy
        </Button>
      </div>
    </div>
  );
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v) => b.includes(v));
}

export function UserFormModal({
  open,
  user,
  onClose,
}: {
  open: boolean;
  /** `null` invites someone new; a row edits that person. */
  user: UserDto | null;
  onClose(): void;
}): ReactElement | null {
  const roles = useRoles();
  const create = useCreateUser();
  const update = useUpdateUser();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleKeys, setRoleKeys] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  /** Set after a successful create that echoed a token; keeps the modal open. */
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  // Re-seed whenever the modal opens for a (different) subject. Keyed on
  // `open` too, so closing and reopening "Invite" does not resurrect a
  // half-typed form from last time.
  useEffect(() => {
    if (!open) return;
    setEmail(user?.email ?? '');
    setName(user?.name ?? '');
    setRoleKeys(user ? user.roles.map((r) => r.key) : []);
    setFieldErrors({});
    setFormError(null);
    setInviteToken(null);
  }, [open, user]);

  if (!open) return null;

  const busy = create.isPending || update.isPending;

  const toggleRole = (key: string): void => {
    setRoleKeys((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  };

  const submit = async (): Promise<void> => {
    setFieldErrors({});
    setFormError(null);
    try {
      if (user === null) {
        const created = await create.mutateAsync({ email: email.trim(), name: name.trim(), roleKeys });
        if (created.developmentInviteToken) {
          // Don't close: the token exists precisely because no email went out,
          // so the link below is the only way the invitee ever hears of this.
          setInviteToken(created.developmentInviteToken);
          return;
        }
        onClose();
        return;
      }

      // Only the changed fields — see the header.
      const changes: UpdateUserRequest = {
        ...(name.trim() !== user.name ? { name: name.trim() } : {}),
        ...(sameSet(
          roleKeys,
          user.roles.map((r) => r.key),
        )
          ? {}
          : { roleKeys }),
      };
      if (Object.keys(changes).length === 0) {
        onClose();
        return;
      }
      await update.mutateAsync({ userRef: user.ref, changes });
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        const bound = err.fieldErrors();
        setFieldErrors(bound);
        // Field errors are already beside their inputs; only an unbound
        // message needs the slot above the form.
        if (Object.keys(bound).length === 0) setFormError(err.message);
      } else {
        setFormError('The panel could not reach the server. Try again.');
      }
    }
  };

  return (
    <Modal
      open={open}
      title={user === null ? 'Invite a user' : `Edit ${user.name}`}
      onClose={onClose}
      footer={
        inviteToken !== null ? (
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void submit()}>
              {user === null ? 'Send invite' : 'Save changes'}
            </Button>
          </>
        )
      }
    >
      {inviteToken !== null ? (
        <InviteAcceptLink token={inviteToken} />
      ) : (
        <form
          className="flex flex-col gap-[var(--space-4)]"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {/* `ErrorState`, not a hand-tinted paragraph: the danger colour is
              confined to the primitives that own error presentation
              (`sourceScan.spec.ts` criterion 25), and this modal is not one. */}
          {formError !== null ? <ErrorState title="That did not save" body={formError} /> : null}

          <Field label="Email" required error={fieldErrors['email']}>
            {(field) =>
              user === null ? (
                <Input
                  {...field}
                  type="email"
                  value={email}
                  autoComplete="off"
                  onChange={(event) => setEmail(event.target.value)}
                />
              ) : (
                // The PATCH schema has no email field — an address is an
                // identity, not a property, so it is shown but not editable.
                <Input {...field} value={email} readOnly disabled />
              )
            }
          </Field>

          <Field label="Name" required error={fieldErrors['name']}>
            {(field) => (
              <Input {...field} value={name} onChange={(event) => setName(event.target.value)} />
            )}
          </Field>

          <Field
            label="Roles"
            required
            hint="At least one. What they can do is the union of everything ticked."
            error={fieldErrors['roleKeys']}
          >
            {() => (
              <div className="flex flex-col gap-[var(--space-2)]">
                {roles.isPending ? (
                  <p className="text-[length:var(--text-xs)] text-muted">Loading roles…</p>
                ) : null}
                {roles.data?.map((role) => (
                  <label
                    key={role.key}
                    className="flex items-center gap-[var(--space-2)] text-[length:var(--text-sm)] text-body"
                  >
                    <Checkbox
                      checked={roleKeys.includes(role.key)}
                      onChange={() => toggleRole(role.key)}
                    />
                    {role.name}
                  </label>
                ))}
              </div>
            )}
          </Field>
        </form>
      )}
    </Modal>
  );
}
